

import React, { useState, useEffect } from "react";
import { Form, Button, Row, Col, Accordion, Badge, Spinner } from "react-bootstrap";
import { Star, Mail, Filter, Plus, MoreHorizontal, Briefcase, Users, Search, Eye } from "lucide-react";
import { getHRPostedJobs } from "../../../../../services/contactServices.js";
import { getStudentAppliedJobs } from "../../../../../services/AppliedStudentsService.js";
import PlacementPopupView from "../../../placement/placementpopup/PlacementPopupView.jsx";


import { getOrganizations, updateOrganization } from "../../../../../services/organizationServices.js";
import OrganizationPopupView from "../../../organization/organizationpopup/OrganizationPopupView.jsx";


const SummaryTab = ({
  contact,
  description,
  setDescription,
  isEditingDescription,
  setIsEditingDescription,
  organizations = [],
  users = [],
}) => {
  const [jobs, setJobs] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [isStudent, setIsStudent] = useState(false);
  const [selectedPlacementForPopup, setSelectedPlacementForPopup] = useState(null);
  const [showPlacementPopup, setShowPlacementPopup] = useState(false);
  const [defaultActiveTab, setDefaultActiveTab] = useState("summary");

  
// Add these new state variables after your existing useState declarations
const [selectedOrganizationForPopup, setSelectedOrganizationForPopup] = useState(null);
const [showOrganizationPopup, setShowOrganizationPopup] = useState(false);
const [defaultActiveOrgTab, setDefaultActiveOrgTab] = useState("summary");

  const lastFetchedId = React.useRef(null);

  // Fetch jobs for this contact
  useEffect(() => {
    if (contact?.id && contact.id !== lastFetchedId.current) {
      fetchJobs();
      lastFetchedId.current = contact.id;
    }
  }, [contact?.id]);

  const fetchJobs = async () => {
    setLoadingJobs(true);
    try {
      // First try fetching HR posted jobs
      const hrResponse = await getHRPostedJobs(contact.id, 1, 10);

      if (hrResponse && hrResponse.data && Array.isArray(hrResponse.data) && hrResponse.data.length > 0) {
        setJobs(hrResponse.data);
        setIsStudent(false);
      } else {
        // If no HR jobs, try fetching student applied jobs
        const studentResponse = await getStudentAppliedJobs(contact.id, 1);
        if (studentResponse && studentResponse.data && Array.isArray(studentResponse.data)) {
          setJobs(studentResponse.data);
          setIsStudent(true);
        } else {
          setJobs([]);
          setIsStudent(false);
        }
      }
    } catch (err) {
      console.error("Failed to load jobs:", err);
      setJobs([]);
    } finally {
      setLoadingJobs(false);
    }
  };

  // fetchOrganizations is no longer needed on mount as data comes with contact record


  const handleViewPlacement = (placement) => {
    if (placement) {
      setSelectedPlacementForPopup(placement);
      setDefaultActiveTab("summary");
      setShowPlacementPopup(true);
    }
  };

  const handleClosePlacementPopup = () => {
    setShowPlacementPopup(false);
    setSelectedPlacementForPopup(null);
    setDefaultActiveTab("summary");
  };

  const handleUpdatePlacement = async (updatedPlacement) => {
    try {
      await fetchJobs();
      setSelectedPlacementForPopup(updatedPlacement);
    } catch (error) {
      console.error("Update failed:", error);
    }
  };

  const handleViewOrganization = (orgData) => {
  if (orgData) {
    setSelectedOrganizationForPopup(orgData);
    setDefaultActiveOrgTab("summary");
    setShowOrganizationPopup(true);
  }
};

const handleCloseOrganizationPopup = () => {
  setShowOrganizationPopup(false);
  setSelectedOrganizationForPopup(null);
  setDefaultActiveOrgTab("summary");
};

const handleUpdateOrganization = async (updatedOrganization) => {
  try {
    await updateOrganization(updatedOrganization.id, updatedOrganization);
    setSelectedOrganizationForPopup(updatedOrganization);
  } catch (error) {
    console.error("Update failed:", error);
  }
};


  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch (error) {
      return "Invalid Date";
    }
  };

  // Display only first 5 jobs
  const displayedJobs = jobs.slice(0, 5);

  return (
    <div className="contact-summary-content">
      <Row className="h-100" style={{ margin: 0 }}>
        {/* Left Column - Contact Details */}
        <Col md={9} style={{ borderRight: "1px solid #e9ecef", paddingRight: "20px", maxHeight: "700px", overflowY: "auto" }}>
          
          {/* Header Row - Labels */}
          <Row className="contact-header-row">
            <Col md={2}>
              <span className="field-label">Contact Name</span>
            </Col>
            <Col md={3}>
              <span className="field-label">Email</span>
            </Col>
            <Col md={2}>
              <span className="field-label">Phone</span>
            </Col>
            <Col md={2}>
              <span className="field-label">Organization</span>
            </Col>
            <Col md={2}>
              <span className="field-label">Assigned To</span>
            </Col>
            <Col md={2}></Col>
          </Row>

          {/* Data Row 1 */}
          <Row className="contact-data-row">
            <Col md={2}>
              <span className="field-value">{contact.name || "N/A"}</span>
            </Col>
            <Col md={3}>
              <span className="field-value">
                {contact.email ? (
                  <a href={`mailto:${contact.email}`} style={{ color: "#ea580c", textDecoration: "none" }}>
                    {contact.email}
                  </a>
                ) : (
                  "N/A"
                )}
              </span>
            </Col>
            <Col md={2}>
              <span className="field-value">
                {contact.phone ? (
                  <a href={`tel:${contact.phone}`} style={{ color: "#ea580c", textDecoration: "none" }}>
                    {contact.phone}
                  </a>
                ) : (
                  "N/A"
                )}
              </span>
            </Col>
            <Col md={2}>
              {(() => {
                const orgName = contact.organizationName || 
                                (Array.isArray(contact.organizationId) ? contact.organizationId[0]?.name : 
                                 (typeof contact.organizationId === 'object' ? contact.organizationId?.name : contact.organizationId)) || 
                                "Not specified";
                
                const isOrgClickable = orgName && 
                                      orgName !== "Not Available" && 
                                      orgName !== "Unknown" && 
                                      orgName !== "Not specified" && 
                                      orgName !== "No Organization" &&
                                      orgName.trim() !== "";

                return (
                  <span 
                    className="field-value"
                    style={{
                      color: isOrgClickable ? "#0d6efd" : "#212529",
                      cursor: isOrgClickable ? "pointer" : "default",
                      fontWeight: isOrgClickable ? "500" : "normal",
                      textDecoration: isOrgClickable ? "underline" : "none"
                    }}
                    onClick={() => {
                      if (!isOrgClickable) return;

                      let orgData = Array.isArray(contact.organizationId) 
                        ? contact.organizationId[0] 
                        : (typeof contact.organizationId === 'object' ? contact.organizationId : null);
                      
                      // If we only have a string ID, find the full object in the organizations prop
                      if (!orgData && typeof contact.organizationId === 'string') {
                        orgData = organizations.find(org => org.id === contact.organizationId);
                      }

                      // Fallback: create a minimal stub if we have an ID/Name
                      if (!orgData) {
                        orgData = { id: contact.id, name: orgName }; // Use contact ID as fallback if we don't have org ID
                      }
                      
                      if (orgData) {
                        handleViewOrganization(orgData);
                      }
                    }}
                  >
                    {orgName}
                  </span>
                );
              })()}
            </Col>

            <Col md={2}>
              <span className="field-value">
                {contact.assignedToName || 
                 (Array.isArray(contact.assignedToId) ? `${contact.assignedToId[0]?.firstName || ""} ${contact.assignedToId[0]?.lastName || ""}`.trim() : 
                  (typeof contact.assignedToId === 'object' ? `${contact.assignedToId?.firstName || ""} ${contact.assignedToId?.lastName || ""}`.trim() : contact.assignedToId)) || 
                 "Not assigned"}
              </span>
            </Col>
            <Col md={2}></Col>
          </Row>

          {/* Additional Row - Labels */}
          <Row className="contact-additional-row mt-3">
            <Col md={3}>
              <span className="field-label">Created At</span>
            </Col>
            <Col md={3}>
              <span className="field-label">Updated At</span>
            </Col>
            <Col md={6}></Col>
          </Row>

          {/* Additional Row - Data */}
          <Row className="contact-data-row">
            <Col md={3}>
              <span className="field-value">
                {contact.createdAt
                  ? new Date(contact.createdAt).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })
                  : "Not available"}
              </span>
            </Col>
            <Col md={3}>
              <span className="field-value">
                {contact.updatedAt
                  ? new Date(contact.updatedAt).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })
                  : "Not available"}
              </span>
            </Col>
            <Col md={6}></Col>
          </Row>
        </Col>

        {/* Right Column - Posted Jobs */}
        <Col md={3} style={{ paddingLeft: "20px", maxHeight: "700px", overflowY: "auto", overflowX: "hidden", display: "flex", flexDirection: "column" }}>
          <div className="jobs-sidebar">
            <h6 className="mb-3" style={{ fontWeight: 600, fontSize: "16px" }}>
              <Briefcase size={18} className="me-2" style={{ display: "inline" }} />
              {isStudent ? "Applied Jobs" : "Posted Jobs"}
            </h6>

            {loadingJobs ? (
              <div className="text-center py-4">
                <Spinner animation="border" variant="primary" size="sm" />
                <p className="text-muted mt-2" style={{ fontSize: "0.85em" }}>
                  Loading jobs...
                </p>
              </div>
            ) : jobs && jobs.length > 0 ? (
              <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
                <div className="mb-3">
                  <small className="text-muted">
                    Total Jobs <span className="badge bg-primary">{jobs.length}</span>
                  </small>
                </div>

                <div className="jobs-list" style={{ maxHeight: "calc(100vh - 350px)", overflowY: "auto", flex: 1 }}>
                  {displayedJobs.map((job, index) => (
                    <div
                      key={job.id || index}
                      className="job-card"
                      style={{
                        border: "1px solid #e9ecef",
                        borderRadius: "6px",
                        padding: "12px",
                        marginBottom: "10px",
                        backgroundColor: "#f8f9fa",
                        cursor: "pointer",
                        transition: "all 0.2s",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = "#e9ecef";
                        e.currentTarget.style.boxShadow = "0 2px 4px rgba(0,0,0,0.1)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "#f8f9fa";
                        e.currentTarget.style.boxShadow = "none";
                      }}
                      onClick={() => handleViewPlacement(job)}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                          gap: "8px",
                        }}
                      >
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div
                            className="job-title"
                            style={{
                              fontWeight: 600,
                              marginBottom: "4px",
                              fontSize: "0.9em",
                              color: "#0d6efd",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {job.jobName || "N/A"}
                          </div>
                        </div>
                        <Button
                          variant="link"
                          size="sm"
                          onClick={() => handleViewPlacement(job)}
                          className="p-0"
                          style={{ color: "#ea580c" }}
                        >
                          <Eye size={14} />
                        </Button>
                      </div>

                      {job.organizationName && (
                        <div
                          style={{
                            fontSize: "11px",
                            color: "#999",
                            marginBottom: "4px",
                            wordBreak: "break-word",
                          }}
                        >
                          {job.organizationName}
                        </div>
                      )}

                      {job.salary && (
                        <div
                          style={{
                            fontSize: "11px",
                            color: "#999",
                            marginBottom: "4px",
                            fontWeight: "500",
                          }}
                        >
                          {job.salary}
                        </div>
                      )}

                      {job.jobLocation && (
                        <div
                          style={{
                            fontSize: "11px",
                            color: "#999",
                            marginBottom: "4px",
                          }}
                        >
                          📍 {job.jobLocation}
                        </div>
                      )}

                      {job.jobPostedDate && (
                        <div
                          style={{
                            fontSize: "11px",
                            color: "#999",
                            marginTop: "6px",
                            paddingTop: "6px",
                            borderTop: "1px solid #e9ecef",
                          }}
                        >
                          Posted: {formatDate(job.jobPostedDate)}
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {jobs.length > 5 && (
                  <Button
                    variant="link"
                    size="sm"
                    className="mt-2"
                    style={{ color: "#ea580c", padding: 0, fontSize: "0.9em" }}
                  >
                    View All ({jobs.length})
                  </Button>
                )}
              </div>
            ) : (
              <div className="text-center py-4" style={{ color: "#6c757d" }}>
                <Briefcase size={32} className="mb-2" style={{ opacity: 0.5 }} />
                <p style={{ fontSize: "14px" }}>No Posted Jobs</p>
              </div>
            )}
          </div>
        </Col>
      </Row>

      {selectedPlacementForPopup && (
        <PlacementPopupView
          show={showPlacementPopup}
          onHide={handleClosePlacementPopup}
          placement={selectedPlacementForPopup}
          onUpdatePlacement={handleUpdatePlacement}
          defaultTab={defaultActiveTab}
        />
      )}

      {selectedOrganizationForPopup && (
  <OrganizationPopupView
    show={showOrganizationPopup}
    onHide={handleCloseOrganizationPopup}
    organization={selectedOrganizationForPopup}
    onUpdateOrganization={handleUpdateOrganization}
    defaultActiveTab={defaultActiveOrgTab}
  />
)}


      <style>{`
        .contact-summary-content {
          padding: 20px 0;
        }

        .contact-header-row {
          border-bottom: 1px solid #e9ecef;
          padding-bottom: 10px;
          margin-bottom: 15px;
        }

        .contact-data-row {
          margin-bottom: 20px;
        }

        .contact-additional-row {
          border-top: 1px solid #e9ecef;
          padding-top: 20px;
          margin-bottom: 30px;
        }

        .field-label {
          font-size: 0.85rem;
          color: #6c757d;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .field-value {
          font-size: 0.9rem;
          color: #212529;
          margin-top: 5px;
          font-weight: 500;
        }

        .badge {
          font-size: 0.75rem;
          padding: 0.25rem 0.5rem;
        }

        .jobs-sidebar {
          background: white;
          border: 1px solid #e9ecef;
          border-radius: 8px;
          padding: 15px;
          display: flex;
          flex-direction: column;
          height: 100%;
          max-height: 700px;
        }

        .jobs-list {
          scroll-behavior: smooth;
        }

        .jobs-list::-webkit-scrollbar {
          width: 6px;
        }

        .jobs-list::-webkit-scrollbar-track {
          background: transparent;
        }

        .jobs-list::-webkit-scrollbar-thumb {
          background: #dee2e6;
          border-radius: 3px;
        }

        .jobs-list::-webkit-scrollbar-thumb:hover {
          background: #adb5bd;
        }

        .job-card {
          transition: all 0.2s ease;
        }

        .job-card:hover {
          transform: translateY(-2px);
        }

        .job-title {
          font-weight: 600;
          margin-bottom: 4px;
          font-size: 0.9em;
        }

        .text-muted {
          color: #6c757d !important;
        }

        a:hover {
          text-decoration: underline;
        }
      `}</style>
    </div>
  );
};

export default SummaryTab;
